/**
 * Floor Plan Card Selected Epic
 *
 * Reacts to floorPlanCardSelected actions and dispatches navigateTo to switch to floor plan
 * detail with the relevant selected lot / context preserved. Every floor plan card (including a
 * Similar Home one under the Sold chip, NHS20-8827) opens the same Available-chip detail
 * experience — highlighting that plan's lots, same as clicking it from its own chip's listing —
 * so when the active chip isn't already Available, this first dispatches chipSelected(Available)
 * (its existing reducer resets to a fresh Available listing) before navigateTo, which then pushes
 * that fresh state as the view-history entry Back returns to, instead of the Sold card the user
 * actually clicked from.
 *
 * @param {object} params
 * @param {object} params.dataActions - Actions from the feature data slice instance
 * @param {Observable} params.store$ - Redux store state observable
 * @returns {import('redux-observable').Epic}
 */

import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ofType } from 'redux-observable';
import { VIEW_FLOOR_PLAN_DETAIL } from '../constants';
import { selectSelectedLotId, selectActiveChip } from './selectors';
import LotStatus from '../enums/lotStatus';

function createFloorPlanCardSelectedEpic({ dataActions, store$ }) {
  return (action$) => action$.pipe(
    ofType(dataActions.floorPlanCardSelected.type),
    withLatestFrom(store$),
    mergeMap(([action, state]) => {
      const selectedLotId = selectSelectedLotId(state) ?? null;
      const activeChip = selectActiveChip(state);

      const planId = Number(action.payload);
      const navigateToAction = dataActions.navigateTo({
        view: VIEW_FLOOR_PLAN_DETAIL,
        context: {
          planId: Number.isFinite(planId) ? planId : null,
          selectedLotId,
          housingUnitId: null,
        },
      });

      return activeChip === LotStatus.Available.toString() || activeChip === LotStatus.Model.toString()
        ? of(navigateToAction)
        : of(dataActions.chipSelected(LotStatus.Available.toString()), navigateToAction);
    }),
  );
}

export default createFloorPlanCardSelectedEpic;
